import { Box, Paper, Stack, Typography } from '@mui/material'
import moment from 'moment'
import React, { useMemo } from 'react'
import { WarningIcon } from 'src/Libs/flow/icons'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthenticationInfo } from '../AuthenticationInfo'
import { MIDCertificationMarker, PROAuthenticationMarker } from '../Marker'
import { ChargingSessionModel } from '../charging-session-models'
import { getSessionStatusTranslation } from '../charging-session-parser'
import { setSessionsForExport } from '../sessions-for-export'
import { formatDuration } from '../utils/format-duration'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'

interface ChargingSessionResponsiveTableProps {
  installationId: number
  chargingSessions: ChargingSessionModel[]
}

/**
 * Responsive table component
 * It is active on tablet and mobile screens view and receives charging sessions
 */
export const ChargingSessionResponsiveTable: React.FC<ChargingSessionResponsiveTableProps> = ({ installationId, chargingSessions }) => {
  const { t } = useCustomTranslation()

  const latestFirstSessions = useMemo(() => {
    return chargingSessions.sort(nullsLast((a, b) => (a.startAt && b.startAt ? a.startAt.localeCompare(b.startAt) : 0))).reverse()
  }, [chargingSessions])

  setSessionsForExport(latestFirstSessions)

  return (
    <Paper sx={{ p: 1 }}>
      {!latestFirstSessions.length ? (
        <Typography textAlign={'center'}>{t('shell.noData')}</Typography>
      ) : (
        latestFirstSessions.map(session => <ChargingSessionResponsiveTableItem key={session.id} session={session} installationId={installationId} />)
      )}
    </Paper>
  )
}

interface ChargingSessionResponsiveTableItemProps {
  installationId: number
  session: ChargingSessionModel
}

/**
 * Responsive table single row item component
 * Receives single charging session and display data from charging session.
 */
const ChargingSessionResponsiveTableItem: React.FC<ChargingSessionResponsiveTableItemProps> = ({ installationId, session }) => {
  const { t } = useCustomTranslation()

  const { openChargingSessionDetailsDialogV1Flow } = useModals()

  const sessionDuration = formatDuration(session.durationMilliseconds, t)
  const sessionStatus = getSessionStatusTranslation(t, session.status)
  const sessionStartAt = session.startAt ? moment(session.startAt).format(t('common.format.date')) : ''
  const infoText = `${sessionStatus}, ${sessionStartAt}, ${sessionDuration}`

  return (
    <Stack
      direction={'row'}
      flexWrap={'wrap'}
      alignItems={'center'}
      columnGap={2}
      rowGap={1}
      sx={{
        p: 1.5,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        ':last-child': {
          border: 'none',
        },
        ':hover': {
          backgroundColor: theme => theme.palette.grey.grey11,
        },
      }}
      // TODO: INTEGRATE DIALOG IN useModals and ModalRoot
      onClick={() => openChargingSessionDetailsDialogV1Flow({ installationId, session })}
    >
      <Stack spacing={1}>
        <Typography>
          {t('emobility.chargingSession.wallbox')}:{' '}
          <Box component={'span'} fontWeight={'bold'}>
            {session.deviceName}
          </Box>
        </Typography>
        <Typography
          color="textSecondary"
          sx={{
            overflowWrap: 'anywhere',
            hyphens: 'auto',
          }}
        >
          {infoText}
        </Typography>
      </Stack>

      <AuthenticationInfo rfid={session.authentication.data ?? null} cardName={session.authentication.cardName} />
      {session.authentication.type === 'OCPP' ? <PROAuthenticationMarker /> : null}

      {session.isMidCertified && <MIDCertificationMarker />}
      {!!session.midAnomaly && (
        <Box>
          <WarningIcon color="warning" sx={{ float: 'left' }} />
          {t('emobility.chargingSession.charge-measurement-anomaly')}
        </Box>
      )}
    </Stack>
  )
}
